import React, { useState } from 'react'
import Chip from '@mui/material/Chip';
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    Grid,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Divider,
    Button,
    Snackbar,
} from '@material-ui/core'
import { Stack, Alert } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import InfoIcon from '@mui/icons-material/Info'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { Auth } from 'aws-amplify'

import IMSFormAccordion from './Accordion/IMSFormAccordion'
import IMSContentLoader from './IMSContentLoader'
import LoadFormPreview from '../LoadFormPreview'
import LightboxDialog from '../../components/LightboxDialog'
import {
    isAdmin,
    isMultipleOrganization,
} from '../../../../Utils/ServerUtil'
import {
    saveIMSForms,
    submitItem,
    savePopulatedForms,
} from '../../../../Utils/SubmitUtil'
import { AutoPopulatedComponentCheck, FieldValues } from './IMSFormFieldsTypes'
import TextField from '@mui/material/TextField'

type ImsFormFieldsProps = {
    handleSaveSuccess: () => void
    prevEvalItem: any
    handleIMSFormSave: () => Promise<void>
    setSaveForm: (value: boolean) => void
    setImsForm: (data: any) => void
    loadForm: boolean
    selectedCategory: any
    successAlert: boolean
    handleSaveSuccessClose: () => void
    imsForm: any
    error: string | null
    loading: boolean
    ims_forms_data: any
    titleStyle?: React.CSSProperties
    internaUseValues: React.CSSProperties
    internaUseValues2: React.CSSProperties
    evaluation: any
    nominationValue: string | number
    handleNominationChange: (
        event: React.ChangeEvent<{ value: unknown }>
    ) => void
    setFieldValues: (values: FieldValues) => void
    fieldValues: FieldValues
    handleFieldChange: (
        groupId: string | number,
        componentId: string | number,
        value: any,
        datatype: string
    ) => void
    newIMSData: any
}

const ImsFormFields = (props: ImsFormFieldsProps) => {
    const {
        loadForm,
        selectedCategory,
        successAlert,
        handleSaveSuccessClose,
        imsForm,
        error,
        loading,
        ims_forms_data,
        titleStyle,
        internaUseValues,
        internaUseValues2,
        evaluation,
        nominationValue,
        handleNominationChange,
        fieldValues,
        setFieldValues,
        handleFieldChange,
        newIMSData,
        handleIMSFormSave,
        setSaveForm,
        setImsForm,
        prevEvalItem,
        handleSaveSuccess,
    } = props

    const [lightboxImage, setLightboxImage] = useState<string | null>(null)
    const [lightboxOpen, setLightboxOpen] = useState<boolean>(false)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [populatedSuccess, setPopulatedSuccess] = useState<boolean>(false)
    const [populateError, setPopulateError] = useState<boolean>(false)
    const [vinInput, setVinInput] = useState<string>('')

    const populateFormAPI = async (itemId: string, vinNumber?: string) => {
        try {
            let jwtToken
            let tokenRefreshError = false

            try {
                const session = await Auth.currentSession()
                jwtToken = session.getIdToken().getJwtToken()
            } catch (e) {
                try {
                    const session = await Auth.currentSession()
                    jwtToken = session.getIdToken().getJwtToken()
                } catch (e) {
                    tokenRefreshError = true
                }
            }

            const headers: Record<string, string> = {
                authorization: jwtToken || '',
                accept: 'application/json',
                'Content-Type': 'application/json',
            }

            if (isAdmin() || isMultipleOrganization()) {
                const organization =
                    localStorage['hw.' + window.btoa('organization')]
                if (organization && window.atob(organization)) {
                    headers['HW-Organization'] = window.atob(organization) ? window.atob(organization) : 'f376c401-6295-4038-a146-e8b52eef6385';
                }
            }

            const filteredHeaders: Record<string, string> = Object.fromEntries(
                Object.entries(headers).filter(
                    ([_, value]) => value !== undefined
                )
            )

            const url = `https://dev.api.pwfieldtool.com/v1/items/${itemId}/populate_form`
            const options: RequestInit = {
                method: 'POST',
                headers: filteredHeaders,
                body: vinNumber
                    ? JSON.stringify({ vin: vinNumber })
                    : undefined,
            }

            const response = await fetch(url, options)
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            return data
        } catch (error) {
            setPopulateError(true)
            throw error
        }
    }

    const handlePopulateFormToIMS = async () => {
        try {
            if (!imsForm?.item?.vin) {
                setIsModalOpen(true)
            } else {
                const populateResponse = await populateFormAPI(
                    imsForm.item.item_id
                )
                if (populateResponse) {
                    const newFieldValues: FieldValues = {}
                    setPopulatedSuccess(true)

                    populateResponse.groups?.forEach((group: any) => {
                        group.components?.forEach((component: any) => {
                            newFieldValues[String(component.id)] = {
                                value: component.component_response,
                                is_auto_populated: true,
                            }
                        })
                    })

                    const updatedFormData = {
                        ...imsForm,
                        item: {
                            ...imsForm.item,
                            ims_forms_data: { ...populateResponse },
                        },
                    }

                    setFieldValues(newFieldValues)
                    setImsForm(updatedFormData)

                    await savePopulatedForms(
                        imsForm.item.item_id,
                        updatedFormData
                    )
                    setSaveForm(true)
                }
            }
        } catch (error) {
            setSaveForm(false)
        }
    }

    const handleModalSubmit = async () => {
        try {
            if (!vinInput.trim()) {
                alert('Please enter a VIN number')
                return
            }

            const populateResponse = await populateFormAPI(
                imsForm.item.item_id,
                vinInput
            )

            if (populateResponse) {
                const saveItemWithVIN = { ...imsForm.item, vin: vinInput }

                await submitItem(
                    imsForm.item.item_id,
                    saveItemWithVIN,
                    imsForm.item.owner,
                    imsForm.item.owner_name,
                    imsForm.item,
                    null
                )

                setIsModalOpen(false)
                setVinInput('')
                setPopulatedSuccess(true)
                const newFieldValues: FieldValues = {}

                populateResponse.groups?.forEach((group: any) => {
                    group.components?.forEach((component: any) => {
                        newFieldValues[String(component.id)] = {
                            value: component.component_response,
                            is_auto_populated: true,
                        }
                    })
                })

                const updatedFormData = {
                    ...imsForm,
                    item: {
                        ...imsForm.item,
                        ims_forms_data: { ...populateResponse },
                    },
                }

                setFieldValues(newFieldValues)
                setImsForm(updatedFormData)

                await savePopulatedForms(imsForm.item.item_id, updatedFormData)
                setSaveForm(true)
            }
        } catch (error) {
            setSaveForm(false)
        }
    }

    const shouldShowAutoPopulatedIndicator = (
        component: AutoPopulatedComponentCheck,
        currentFieldValues: FieldValues
    ): boolean => {
        if (component.id === null || component.id === undefined) {
            return false
        }

        const componentIdStr = String(component.id)
        const fieldValueData = currentFieldValues[componentIdStr]

        return !!(
            component.is_auto_populated &&
            (!fieldValueData ||
                fieldValueData === component.component_response ||
                (typeof fieldValueData === 'object' &&
                    fieldValueData?.value ===
                        component.component_response?.value))
        )
    }

    const handlePopulateClose = () => {
        setPopulatedSuccess(false)
    }

    const handlePopulateErrorClose = () => {
        setPopulateError(false)
    }

    console.log({
        ims_forms_data,
        imsForm,
        prevEvalItem,
        selectedCategory,
        fieldValues,
        newIMSData,
    })

    const formData =
        ims_forms_data || newIMSData || imsForm?.item?.ims_forms_data
    console.log({ formData })
    const groups = formData?.groups

    if (!selectedCategory) {
        return (
            <div
                style={{
                    padding: '20px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    background: '#f9f9f9',
                    maxHeight: '95%',
                    overflowY: 'auto',
                    marginLeft: '20px',
                    marginRight: '20px',
                    marginTop: '20px',
                }}
            >
                <Snackbar
                    open={successAlert}
                    autoHideDuration={6000}
                    onClose={handleSaveSuccessClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleSaveSuccessClose}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        IMS form is saved successfully!
                    </Alert>
                </Snackbar>
                <LoadFormPreview />
            </div>
        )
    }

    if (loading || !groups) {
        return (
            <div
                style={{
                    padding: '20px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    background: '#f9f9f9',
                    maxHeight: '95%',
                    overflowY: 'auto',
                    marginLeft: '20px',
                    marginRight: '20px',
                    marginTop: '20px',
                }}
            >
                <Snackbar
                    open={populateError}
                    autoHideDuration={6000}
                    onClose={handlePopulateErrorClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handlePopulateErrorClose}
                        severity="error"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        Wrong VIN number provided.
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={populatedSuccess}
                    autoHideDuration={6000}
                    onClose={handlePopulateClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handlePopulateClose}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        Form Populated Successfully
                    </Alert>
                </Snackbar>
                <Snackbar
                    open={successAlert}
                    autoHideDuration={6000}
                    onClose={handleSaveSuccessClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleSaveSuccessClose}
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        IMS form is saved successfully!
                    </Alert>
                </Snackbar>
                <Dialog
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '16px',
                            maxWidth: '500px',
                            width: '100%',
                        },
                    }}
                >
                    <DialogTitle
                        style={{
                            color: '#4a287d',
                            fontWeight: 600,
                            fontSize: '1.5rem',
                            paddingBottom: '8px',
                        }}
                    >
                        Enter VIN Number
                    </DialogTitle>
                    <DialogContent style={{ paddingTop: '16px' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="VIN Number"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={vinInput}
                            onChange={(e) => setVinInput(e.target.value)}
                            sx={
                                {
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: '#4a287d',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#4a287d',
                                        },
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#4a287d',
                                    },
                                } as any
                            } // Type assertion (use only if necessary)
                        />
                    </DialogContent>
                    <DialogActions
                        style={{ padding: '16px', paddingTop: '16px' }}
                    >
                        <Button
                            onClick={() => setIsModalOpen(false)}
                            style={{
                                color: '#4a287d',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingLeft: '24px',
                                paddingRight: '24px',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleModalSubmit}
                            variant="contained"
                            style={{
                                backgroundColor: '#4a287d',
                                textTransform: 'none',
                                fontWeight: 500,
                                paddingLeft: '24px',
                                paddingRight: '24px',
                            }}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>
                {loading ? (
                    <IMSContentLoader />
                ) : (
                    <div>Error: {error || 'Form data not available'}</div>
                )}
            </div>
        )
    }

    return (
        <div
            style={{
                padding: '20px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                background: '#f9f9f9',
                maxHeight: '95%',
                height: 'calc(100vh - 230px)',
                overflowY: 'auto',
                marginLeft: '20px',
                marginRight: '20px',
                marginTop: '20px',
            }}
        >
            <Snackbar
                open={populateError}
                autoHideDuration={6000}
                onClose={handlePopulateErrorClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handlePopulateClose}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Wrong VIN number provided.
                </Alert>
            </Snackbar>
            <Snackbar
                open={populatedSuccess}
                autoHideDuration={6000}
                onClose={handlePopulateClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handlePopulateClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Form Populated Successfully
                </Alert>
            </Snackbar>
            <Snackbar
                open={successAlert}
                autoHideDuration={6000}
                onClose={handleSaveSuccessClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSaveSuccessClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    IMS form is saved successfully!
                </Alert>
            </Snackbar>
            <Dialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                PaperProps={{
                    style: {
                        borderRadius: '12px',
                        padding: '16px',
                        maxWidth: '500px',
                        width: '100%',
                    },
                }}
            >
                <DialogTitle
                    style={{
                        color: '#4a287d',
                        fontWeight: 600,
                        fontSize: '1.5rem',
                        paddingBottom: '8px',
                    }}
                >
                    Enter VIN Number
                </DialogTitle>
                <DialogContent style={{ paddingTop: '16px' }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="VIN Number"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={vinInput}
                        onChange={(e) => setVinInput(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': { borderColor: '#4a287d' },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4a287d',
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#4a287d',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions style={{ padding: '16px', paddingTop: '16px' }}>
                    <Button
                        onClick={() => setIsModalOpen(false)}
                        style={{
                            color: '#4a287d',
                            textTransform: 'none',
                            fontWeight: 500,
                            paddingLeft: '24px',
                            paddingRight: '24px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleModalSubmit}
                        variant="contained"
                        style={{
                            backgroundColor: '#4a287d',
                            textTransform: 'none',
                            fontWeight: 500,
                            paddingLeft: '24px',
                            paddingRight: '24px',
                        }}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {lightboxOpen && lightboxImage && (
                <LightboxDialog
                    open={lightboxOpen}
                    image={lightboxImage}
                    onClose={() => setLightboxOpen(false)}
                />
            )}

            <div style={{ marginBottom: '20px' }}>
                <Grid
                    container
                    spacing={3}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item xs={6}>
                        <Typography style={titleStyle}>
                            IMS Form Category
                        </Typography>
                        <h2>{formData?.category}</h2>
                    </Grid>
                    <Grid
                        item
                        xs={6}
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <Button
                            variant="contained"
                            endIcon={<AutoFixHighIcon sx={{ marginLeft: 1 }} />}
                            onClick={handlePopulateFormToIMS}
                            className="fourth-button"
                            style={{ height: 40, width: '80%' }}
                        >
                            <Typography
                                style={{ color: 'white' }}
                                variant="button"
                            >
                                {loading ? 'Processing...' : 'Populate Form'}
                            </Typography>
                        </Button>
                    </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0' }} />

                <Grid container spacing={3} alignItems="center">
                    <Grid container item xs={6} direction="column">
                        <Grid item>
                            <Typography style={titleStyle}>Fee Code</Typography>
                            <h4 style={internaUseValues}>
                                {evaluation?.evaluation?.fee_code || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>Location</Typography>
                            <h4 style={internaUseValues}>
                                {imsForm?.item?.location || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                DC Service
                            </Typography>
                            <h4 style={internaUseValues}>
                                {evaluation?.evaluation?.primary_contact ||
                                    '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                Copart Lot Number
                            </Typography>
                            <h4 style={internaUseValues}>
                                {imsForm?.lot_number || '--------'}
                            </h4>
                        </Grid>
                    </Grid>

                    <Grid container item xs={6} direction="column">
                        <Grid item>
                            <Typography style={titleStyle}>
                                Form Status
                            </Typography>
                            <div style={internaUseValues2}>
                                {imsForm?.item?.form_sub ? (
                                    <Chip
                                        color="success"
                                        icon={<CheckIcon />}
                                        label="Form Submitted to IMS"
                                        size="small"
                                        style={{ fontWeight: "light" }}
                                    />
                                ) : (
                                    <Chip
                                        icon={<InfoIcon />}
                                        label="Form is not Submitted"
                                        size="small"
                                    />
                                )}
                            </div>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                Opportunity
                            </Typography>
                            <h4 style={internaUseValues}>
                                {formData?.opportunity_id || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>Job Code</Typography>
                            <h4 style={internaUseValues}>
                                {imsForm?.jobcode || '--------'}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Typography style={titleStyle}>
                                Nomination
                            </Typography>
                            <FormControl
                                style={{ position: 'relative', width: '100%' }}
                            >
                                <Select
                                    labelId="nomination-label"
                                    id="nomination-select"
                                    value={nominationValue}
                                    onChange={handleNominationChange}
                                    style={{
                                        width: '70%',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            style: { maxHeight: 200 },
                                        },
                                    }}
                                >
                                    {[...Array(11)].map((_, index) => (
                                        <MenuItem key={index} value={index}>
                                            {index}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            </div>

            {groups && groups.length > 0 ? (
                <IMSFormAccordion
                    groups={groups}
                    fieldValues={fieldValues}
                    handleFieldChange={handleFieldChange}
                    shouldShowAutoPopulatedIndicator={
                        shouldShowAutoPopulatedIndicator
                    }
                    setLightboxImage={setLightboxImage}
                    setLightboxOpen={setLightboxOpen}
                />
            ) : (
                <div>No form fields available for this category.</div>
            )}
        </div>
    )
}

export default ImsFormFields
